import type { ChannelSearchItem } from "./channel-search.shared";

/**
 * SR6 and SR10's selection arithmetic, as pure functions over id lists.
 *
 * ⛔ EVERY FUNCTION HERE COMPARES `id` AND ONLY `id`. Nothing reads `label`,
 * `secondary` or any pair of them. That is the whole of WAT-11767 → WAT-11771
 * → WAT-11775: the display text is not unique, the name + channel-number pair
 * is not unique either (a channel number is a tuner POSITION, and the live
 * feed carries "Sports Mix" twice on 205), so only a caller-minted opaque id
 * can key a selection.
 *
 * Pure and React-free, so the limit behaviour is testable without rendering.
 */

/** SR10 — true once the content team's limit is reached. No limit, never true. */
export function isAtSelectionLimit(
  selectedIds: readonly string[],
  maxSelections?: number
): boolean {
  return maxSelections !== undefined && selectedIds.length >= maxSelections;
}

/**
 * SR6 — choosing a suggestion selects it; choosing a selected one removes it.
 * SR10 — at the limit a further pick is IGNORED, and `undefined` is how that
 * is said, so the caller does not fire a change event for nothing. Removing is
 * always allowed, which is what re-enables the field.
 */
export function toggleSelection(
  selectedIds: readonly string[],
  id: string,
  maxSelections?: number
): string[] | undefined {
  if (selectedIds.includes(id)) {
    return selectedIds.filter(selected => selected !== id);
  }
  if (isAtSelectionLimit(selectedIds, maxSelections)) {
    return undefined;
  }
  return [...selectedIds, id];
}

/**
 * SR5 + SR10 — the select-all row adds every match, and only up to the limit.
 * Already-selected matches are left where they are rather than re-appended, so
 * the chip order a reader built is not reshuffled by a select-all.
 */
export function addAllSelections(
  selectedIds: readonly string[],
  ids: readonly string[],
  maxSelections?: number
): string[] {
  const next = [...selectedIds];
  for (const id of ids) {
    if (next.includes(id)) {
      continue;
    }
    if (isAtSelectionLimit(next, maxSelections)) {
      break;
    }
    next.push(id);
  }
  return next;
}

/**
 * The selected items, in SELECTION order rather than `items` order — a chip
 * row that reorders itself when the caller re-sorts its source is disorienting.
 * An id with no item is dropped: the caller may replace `items` (a new ZIP, a
 * new market) while a selection made against the old set is still held.
 */
export function selectedItemsOf(
  items: readonly ChannelSearchItem[],
  selectedIds: readonly string[]
): ChannelSearchItem[] {
  const byId = new Map(items.map(item => [item.id, item]));
  const selected: ChannelSearchItem[] = [];
  for (const id of selectedIds) {
    const item = byId.get(id);
    if (item) {
      selected.push(item);
    }
  }
  return selected;
}
